import React, { useState, useRef, useEffect } from 'react';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import {
  addDoc, collection, doc, setDoc, serverTimestamp, updateDoc,
  onSnapshot, orderBy, query, deleteDoc, getDocs, arrayUnion
} from 'firebase/firestore';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  MessageCircle, Send, Search, ChevronLeft, BookOpen, Inbox, Plus, X, User, MoreVertical, Trash2
} from 'lucide-react';

export default function AuthorMessagesTab({ initialReaderId }) {
  const { authorId } = useAuth();
  const { messages: threads, books, readers } = useAuthorDashboard();
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Delete conversation state
  const [deleteThread, setDeleteThread] = useState(null); // thread to delete
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openKebabId, setOpenKebabId] = useState(null); // track which kebab menu is open

  // Compose new conversation state
  const [showCompose, setShowCompose] = useState(false);
  const [composeReaderSearch, setComposeReaderSearch] = useState('');
  const [composeReaderId, setComposeReaderId] = useState(null);
  const [composeReaderName, setComposeReaderName] = useState('');
  const [composeReaderEmail, setComposeReaderEmail] = useState('');
  const [composeBookId, setComposeBookId] = useState('');
  const [composeText, setComposeText] = useState('');

  // Handle initialReaderId from Readers tab
  useEffect(() => {
    if (!initialReaderId || !readers.length) return;
    const reader = readers.find(r => r.id === initialReaderId);
    if (!reader) return;
    // Check for existing thread
    const existingThread = threads.find(t => t.readerId === initialReaderId);
    if (existingThread) {
      setSelectedThreadId(existingThread.id);
    } else {
      setShowCompose(true);
      setComposeReaderId(initialReaderId);
      setComposeReaderName(reader.name || reader.firstName || 'Reader');
      setComposeReaderEmail(reader.email || reader.emailCorrespondence || '');
    }
  }, [initialReaderId, readers, threads]);

  // Load messages for the selected thread
  useEffect(() => {
    if (!selectedThreadId || !authorId) { setThreadMessages([]); return; }
    const q = query(
      collection(db, 'authors', authorId, 'messages', selectedThreadId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const items = [];
      snap.forEach(d => items.push({ id: d.id, ...d.data() }));
      setThreadMessages(items);
    });
    return unsub;
  }, [selectedThreadId, authorId]);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [threadMessages.length]);

  // Mark thread as read when selected
  useEffect(() => {
    if (!selectedThreadId || !authorId) return;
    const thread = threads.find(t => t.id === selectedThreadId);
    if (thread?.unreadByAuthor) {
      updateDoc(doc(db, 'authors', authorId, 'messages', selectedThreadId), {
        unreadByAuthor: false,
      }).catch(() => {});
    }
  }, [selectedThreadId, authorId, threads]);

  // Filtered threads + reader search
  const filteredThreads = threads.filter(t => {
    // Exclude threads deleted for this user
    if ((t.deletedFor || []).includes(authorId)) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (t.readerName || '').toLowerCase().includes(q) ||
      (t.readerEmail || '').toLowerCase().includes(q) ||
      (t.bookTitle || '').toLowerCase().includes(q);
  });

  const composeFilteredReaders = readers.filter(r => {
    if (!composeReaderSearch) return true;
    const q = composeReaderSearch.toLowerCase();
    return (r.name || '').toLowerCase().includes(q) ||
      (r.firstName || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.emailCorrespondence || '').toLowerCase().includes(q);
  }).slice(0, 10);

  const activeThread = selectedThreadId
    ? threads.find(t => t.id === selectedThreadId)
    : null;

  async function handleSend() {
    if (!messageText.trim() || !selectedThreadId || !authorId) return;
    setIsSending(true);
    try {
      await addDoc(collection(db, 'authors', authorId, 'messages', selectedThreadId, 'messages'), {
        content: messageText.trim(),
        sender: 'author',
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'authors', authorId, 'messages', selectedThreadId), {
        lastMessage: messageText.trim(),
        updatedAt: serverTimestamp(),
        unreadByReader: true,
        unreadByAuthor: false,
      });
      if (activeThread?.readerId) {
        await updateDoc(
          doc(db, 'readers', activeThread.readerId, 'messageThreads', selectedThreadId),
          { lastMessage: messageText.trim(), updatedAt: serverTimestamp(), unreadByReader: true }
        ).catch(() => {});
      }
      setMessageText('');
    } catch (err) {
      console.error('Send error:', err);
    } finally {
      setIsSending(false);
    }
  }

  async function handleComposeSubmit() {
    if (!composeText.trim() || !composeReaderId || !authorId) return;
    setIsSending(true);
    try {
      const selectedBook = books.find(b => b.id === composeBookId);
      const threadData = {
        authorId,
        readerId: composeReaderId,
        readerName: composeReaderName,
        readerEmail: composeReaderEmail,
        bookId: composeBookId || '',
        bookTitle: selectedBook?.title || '',
        lastMessage: composeText.trim(),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        unreadByAuthor: false,
        unreadByReader: true,
        messageType: 'book_feedback',
      };
      const threadRef = await addDoc(collection(db, 'authors', authorId, 'messages'), threadData);
      const threadId = threadRef.id;

      // Add message
      await addDoc(collection(db, 'authors', authorId, 'messages', threadId, 'messages'), {
        content: composeText.trim(),
        sender: 'author',
        createdAt: serverTimestamp(),
      });

      // Dual-write reader index
      await setDoc(doc(db, 'readers', composeReaderId, 'messageThreads', threadId), {
        authorId,
        authorName: 'Author',
        bookId: composeBookId || '',
        bookTitle: selectedBook?.title || '',
        threadType: 'author',
        lastMessage: composeText.trim(),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        unreadByReader: true,
      });

      // Reset compose and select new thread
      setShowCompose(false);
      setComposeReaderId(null);
      setComposeReaderName('');
      setComposeReaderEmail('');
      setComposeReaderSearch('');
      setComposeBookId('');
      setComposeText('');
      setSelectedThreadId(threadId);
    } catch (err) {
      console.error('Compose error:', err);
    } finally {
      setIsSending(false);
    }
  }

  // Close kebab menu when clicking outside
  useEffect(() => {
    if (!openKebabId) return;
    const handleClick = () => setOpenKebabId(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [openKebabId]);

  function openDeleteConfirm(thread, e) {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    setDeleteThread(thread);
    setShowDeleteDialog(true);
    setOpenKebabId(null);
  }

  async function handleDeleteForMe() {
    if (!deleteThread || !authorId) return;
    setIsDeleting(true);
    try {
      await updateDoc(doc(db, 'authors', authorId, 'messages', deleteThread.id), {
        deletedFor: arrayUnion(authorId),
      });
      if (selectedThreadId === deleteThread.id) setSelectedThreadId(null);
    } catch (err) {
      console.error('Delete for me error:', err);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteThread(null);
    }
  }

  async function handleDeleteForEveryone() {
    if (!deleteThread || !authorId) return;
    setIsDeleting(true);
    try {
      // Delete all messages in the subcollection
      const msgsSnap = await getDocs(
        collection(db, 'authors', authorId, 'messages', deleteThread.id, 'messages')
      );
      for (const msgDoc of msgsSnap.docs) {
        await deleteDoc(msgDoc.ref);
      }
      // Delete the thread document
      await deleteDoc(doc(db, 'authors', authorId, 'messages', deleteThread.id));
      // Delete the reader's thread index
      if (deleteThread.readerId) {
        await deleteDoc(
          doc(db, 'readers', deleteThread.readerId, 'messageThreads', deleteThread.id)
        ).catch(() => {});
      }
      if (selectedThreadId === deleteThread.id) setSelectedThreadId(null);
    } catch (err) {
      console.error('Delete for everyone error:', err);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteThread(null);
    }
  }

  // Kebab menu component
  function KebabMenu({ thread, position = 'left' }) {
    const isOpen = openKebabId === `${thread.id}-${position}`;
    return (
      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpenKebabId(isOpen ? null : `${thread.id}-${position}`);
          }}
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          title="More options"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
        {isOpen && (
          <div
            className={`absolute top-full mt-1 z-50 w-48 rounded-lg border border-slate-200 bg-white py-1 shadow-lg ${
              position === 'right' ? 'right-0' : 'left-0'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={(e) => openDeleteConfirm(thread, e)}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete conversation
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { if (!open) { setShowDeleteDialog(false); setDeleteThread(null); } }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Delete conversation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">
              This will remove the conversation{deleteThread?.readerName ? ` with ${deleteThread.readerName}` : ''} from your inbox. The other party can still see it.
            </p>
            <div className="flex gap-2 sm:justify-end">
              <Button
                variant="outline"
                onClick={() => { setShowDeleteDialog(false); setDeleteThread(null); }}
                disabled={isDeleting}
                className="border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteForMe}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete conversation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Messages</h1>
        {!showCompose && (
          <Button onClick={() => { setShowCompose(true); setSelectedThreadId(null); }}
            className="bg-teal-600 hover:bg-teal-700 text-white">
            <Plus className="mr-1.5 h-4 w-4" /> New Message
          </Button>
        )}
      </div>

      {/* Compose new conversation */}
      {showCompose && (
        <Card className="p-5 space-y-4 border-teal-200 bg-teal-50/30">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">New conversation</h3>
            <button onClick={() => { setShowCompose(false); setComposeReaderId(null); setComposeReaderSearch(''); setComposeText(''); }}
              className="text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Reader selector */}
          {!composeReaderId ? (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Select a reader</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-9 text-sm"
                  value={composeReaderSearch}
                  onChange={e => setComposeReaderSearch(e.target.value)}
                  placeholder="Search readers by name or email..."
                />
              </div>
              <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-50">
                {composeFilteredReaders.length === 0 ? (
                  <p className="p-3 text-sm text-slate-500 text-center">No readers found</p>
                ) : (
                  composeFilteredReaders.map(r => (
                    <button
                      key={r.id}
                      onClick={() => {
                        setComposeReaderId(r.id);
                        setComposeReaderName(r.name || r.firstName || 'Reader');
                        setComposeReaderEmail(r.email || r.emailCorrespondence || '');
                        setComposeReaderSearch('');
                      }}
                      className="flex w-full items-center gap-3 p-3 text-left hover:bg-slate-50"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-sm font-medium text-slate-600">
                        {(r.name || r.firstName || 'R').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{r.name || r.firstName || 'Reader'}</p>
                        <p className="text-xs text-slate-500 truncate">{r.email || r.emailCorrespondence || ''}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Selected reader */}
              <div className="flex items-center gap-3 rounded-lg bg-white border border-slate-200 px-3 py-2">
                <User className="h-4 w-4 text-teal-600" />
                <span className="text-sm text-slate-700 flex-1">
                  {composeReaderName}
                  <span className="text-slate-400 ml-1">{composeReaderEmail}</span>
                </span>
                <button onClick={() => { setComposeReaderId(null); setComposeReaderName(''); setComposeReaderEmail(''); }}
                  className="text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
              </div>

              {/* Optional book selector */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">About a book (optional)</label>
                <select
                  value={composeBookId}
                  onChange={e => setComposeBookId(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="">General message</option>
                  {books.filter(b => b.status !== 'archived').map(b => (
                    <option key={b.id} value={b.id}>{b.title}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <textarea
                value={composeText}
                onChange={e => setComposeText(e.target.value)}
                placeholder="Write your message..."
                rows={3}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-400 focus:ring-2 focus:ring-teal-100 outline-none"
              />
              <Button
                onClick={handleComposeSubmit}
                disabled={!composeText.trim() || isSending}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Send className="mr-1.5 h-4 w-4" /> Send Message
              </Button>
            </div>
          )}
        </Card>
      )}

      <div className="grid h-[600px] grid-cols-3 gap-0 overflow-hidden rounded-xl border border-slate-200 bg-white">
        {/* Thread list */}
        <div className={`border-r border-slate-200 flex flex-col ${selectedThreadId ? 'hidden md:flex' : ''}`}>
          <div className="border-b border-slate-100 p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9 text-sm" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search readers or books..." />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredThreads.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">
                <Inbox className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                {threads.length === 0
                  ? 'No conversations yet — use "New Message" to start one'
                  : 'No matching conversations'}
              </div>
            ) : (
              filteredThreads.map(thread => (
                <div
                  key={thread.id}
                  onClick={() => { setSelectedThreadId(thread.id); setShowCompose(false); }}
                  className={`flex w-full items-center gap-3 border-b border-slate-50 p-3 text-left hover:bg-slate-50 cursor-pointer ${
                    selectedThreadId === thread.id ? 'bg-teal-50' : ''
                  }`}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-medium text-slate-600">
                    {(thread.readerName || 'R').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="truncate text-sm font-medium text-slate-900">{thread.readerName || 'Reader'}</p>
                      <div className="flex items-center gap-1 shrink-0">
                        {thread.unreadByAuthor && (
                          <span className="flex h-2.5 w-2.5 rounded-full bg-teal-600" />
                        )}
                        <KebabMenu thread={thread} position="left" />
                      </div>
                    </div>
                    {thread.bookTitle && (
                      <p className="flex items-center gap-1 text-[11px] text-teal-600 truncate">
                        <BookOpen className="h-3 w-3" />
                        {thread.bookTitle}
                      </p>
                    )}
                    <p className="truncate text-xs text-slate-500">
                      {thread.lastMessage || 'No messages'}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Message view */}
        <div className={`col-span-2 flex flex-col ${!selectedThreadId ? 'hidden md:flex' : ''}`}>
          {selectedThreadId && activeThread ? (
            <>
              <div className="flex items-center gap-3 border-b border-slate-100 p-3">
                <button
                  onClick={() => setSelectedThreadId(null)}
                  className="rounded p-1 hover:bg-slate-100 md:hidden"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-sm font-medium text-slate-600">
                  {(activeThread.readerName || 'R').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900">{activeThread.readerName || 'Reader'}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {activeThread.readerEmail}
                    {activeThread.bookTitle && ` · re: ${activeThread.bookTitle}`}
                  </p>
                </div>
                <KebabMenu thread={activeThread} position="right" />
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {threadMessages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'author' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 text-sm ${
                      msg.sender === 'author'
                        ? 'rounded-br-sm bg-teal-600 text-white'
                        : 'rounded-bl-sm bg-slate-100 text-slate-900'
                    }`}>
                      <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      <p className={`mt-1 text-[10px] ${msg.sender === 'author' ? 'text-teal-200' : 'text-slate-400'}`}>
                        {msg.createdAt?.toDate?.().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        {' · '}
                        {msg.createdAt?.toDate?.().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <div className="border-t border-slate-100 p-3">
                <div className="flex gap-2">
                  <Input
                    value={messageText}
                    onChange={e => setMessageText(e.target.value)}
                    placeholder="Type a message..."
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={isSending || !messageText.trim()}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageCircle className="mx-auto mb-2 h-10 w-10" />
                <p className="text-sm">Select a conversation to view messages</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
